import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Flame, ChevronLeft, ChevronRight, Snowflake, HeartCrack } from 'lucide-react'
import { useState } from 'react'
import { STREAK_TUCOIN_BONUSES, nextStreakMilestone } from '@tfa/shared'
import { TuCoinIcon } from '../TuCoinIcon'
import { api } from '../../services/api'

/** Локальная дата YYYY-MM-DD (как отображается в календаре). */
function fmtDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Длина серии активных дней, заканчивающейся в `date` (вкл.). Замороженные дни
 * и дни, «оплаченные» жизнью (#290), мостят серию, но в счётчик не идут — как
 * в Duolingo и как на бэкенде (computeCurrentStreak).
 */
function runLengthEndingAt(date: Date, activeDays: Set<string>, bridgeDays: Set<string>): number {
  let n = 0
  const d = new Date(date)
  while (activeDays.has(fmtDay(d)) || bridgeDays.has(fmtDay(d))) {
    if (activeDays.has(fmtDay(d))) n++
    d.setDate(d.getDate() - 1)
  }
  return n
}

const F = 'Roboto, sans-serif'

// Лестница milestone-бонусов TuCoin по непрерывному стрику (дни подряд).
// Источник истины — @tfa/shared. Покрывает весь путь ТФА-1 → ТФА-2 → Клуб (до 360 дн).
const MILESTONES = Object.entries(STREAK_TUCOIN_BONUSES)
  .map(([d, r]) => ({ day: Number(d), reward: r }))
  .sort((a, b) => a.day - b.day)
// Дни серии, на которых начисляется монетка (для отметки в календаре).
const MILESTONE_DAYS = new Set(MILESTONES.map((m) => m.day))

function pluralDays(n: number): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return 'день'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'дня'
  return 'дней'
}

/**
 * Горизонтальный ряд milestone-бонусов TuCoin. Иконки-монеты с тремя состояниями:
 * достигнуто (opacity 1, deep-blue), ближайшая цель (акцент), заблокировано (opacity ~0.3).
 * Авто-скролл к ближайшей цели при открытии.
 */
function MilestoneTrack({ streakDays, claimed }: { streakDays: number; claimed: number[] }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const nextRef = useRef<HTMLDivElement>(null)
  // #339: «получено» — по ledger (бонус один раз за жизнь), а не по длине
  // текущей серии; цель — первая ещё НЕ полученная веха выше текущей длины.
  const claimedSet = useMemo(() => new Set(claimed), [claimed])
  const nextDay = nextStreakMilestone(streakDays, claimed)?.day ?? null

  useEffect(() => {
    const el = nextRef.current
    const cont = scrollRef.current
    if (el && cont) {
      cont.scrollTo({ left: el.offsetLeft - cont.clientWidth / 2 + el.clientWidth / 2, behavior: 'smooth' })
    }
  }, [streakDays])

  return (
    <div style={{
      background: 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: '1px solid rgba(74,108,247,0.16)',
      borderRadius: 16,
      padding: '12px 4px 12px 12px',
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#1A3A5C', marginBottom: 2, paddingRight: 8 }}>
        Будь в режиме дольше
      </div>
      <div style={{ fontSize: 11, color: '#6B7B8D', marginBottom: 10, paddingRight: 8 }}>
        Чем длиннее серия — тем больше наград. Прокрути вправо ⟶
      </div>
      <div
        ref={scrollRef}
        style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {MILESTONES.map((m) => {
          // #339: галочка «получено» — по ledger; веха прошлой серии остаётся
          // полученной и после обрыва (повторно бонус не выдаётся).
          const done = claimedSet.has(m.day)
          const isNext = m.day === nextDay
          const tone = done ? '#1A3A5C' : isNext ? '#4A6CF7' : '#9AA5B4'
          return (
            <div
              key={m.day}
              ref={isNext ? nextRef : undefined}
              style={{
                flexShrink: 0,
                width: 64,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 5,
                padding: '10px 4px 8px',
                borderRadius: 14,
                background: done
                  ? 'rgba(74,108,247,0.10)'
                  : isNext
                    ? 'rgba(74,108,247,0.16)'
                    : 'rgba(26,58,92,0.035)',
                // #275: следующая цель — пунктирная рамка (понятнее, что это цель, а не достигнутое).
                border: isNext ? '1.5px dashed #4A6CF7' : done ? '1px solid rgba(74,108,247,0.22)' : '1px solid rgba(26,58,92,0.06)',
                boxShadow: isNext ? '0 4px 14px rgba(74,108,247,0.28)' : 'none',
                opacity: done || isNext ? 1 : 0.55,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 800, color: tone, lineHeight: 1 }}>+{m.reward}</span>
              <TuCoinIcon size={28} glow={false} style={{ opacity: done ? 1 : isNext ? 0.95 : 0.3 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: tone, lineHeight: 1 }}>{m.day}д</span>
              <span style={{ fontSize: 9, color: isNext ? '#4A6CF7' : '#9AA5B4', lineHeight: 1, fontWeight: isNext ? 700 : 400 }}>
                {isNext ? 'цель' : done ? 'получено' : pluralDays(m.day)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface Props {
  open: boolean
  onClose: () => void
  streakDays: number
  streakRecord: number
  lives: number
}

/**
 * История «в режиме» — помесячный календарь огонёчков.
 * Чем больше дней подряд в режиме (от начала текущей серии), тем горячее пламя.
 * Шкала: <3 дн (жёлтый) → 3–6 дн (оранжевый) → 7–20 дн (красный градиент) → 21+ (ярко-красный).
 */

// 0–4: empty / spark / small / medium / blazing
function fireBucket(daysIntoStreak: number): 0 | 1 | 2 | 3 | 4 {
  if (daysIntoStreak <= 0) return 0
  if (daysIntoStreak < 3) return 1
  if (daysIntoStreak < 7) return 2
  if (daysIntoStreak < 21) return 3
  return 4
}

const BUCKET_STYLE: Record<0 | 1 | 2 | 3 | 4, { bg: string; color: string; fill: string; glow: string }> = {
  0: { bg: 'transparent',                                          color: 'rgba(26,58,92,0.18)', fill: 'transparent',          glow: 'none' },
  1: { bg: 'rgba(255,222,164,0.40)',                               color: '#FFB300',             fill: '#FFE082',              glow: 'none' },
  2: { bg: 'rgba(255,179,82,0.55)',                                color: '#FF7A00',             fill: '#FFC180',              glow: '0 0 0 1px rgba(255,149,0,0.25)' },
  3: { bg: 'linear-gradient(135deg,#FF9500,#FF6B00)',              color: 'white',               fill: 'rgba(255,255,255,0.85)', glow: '0 3px 10px rgba(255,107,0,0.30)' },
  4: { bg: 'linear-gradient(135deg,#FF3B30,#C70021)',              color: 'white',               fill: '#FFD60A',              glow: '0 4px 14px rgba(255,59,48,0.45), 0 0 0 1.5px rgba(255,214,10,0.55) inset' },
}

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

// Build a single month grid (6 rows × 7 cols, Mon-first), with day numbers and meta-info.
interface MonthCell {
  date: Date | null
  dayNumber: number
  inStreak: boolean
  frozen: boolean
  lifeLost: boolean
  daysIntoStreak: number
  isToday: boolean
  isFuture: boolean
}

// Календарь строится из РЕАЛЬНОЙ истории активных дней (activeDays), а не из
// текущего streakDays — поэтому видны и прошлые/прерванные серии (до дня потери),
// а не только последняя. Интенсивность пламени = длина серии, кончающейся в этот день.
// frozenDays — пропуски, спасённые заморозкой (снежинка); lifeLostDays (#290) —
// пропуски, «оплаченные» жизнью (разбитое сердце). И те и другие мостят серию.
function buildMonth(year: number, month: number, activeDays: Set<string>, frozenDays: Set<string>, lifeLostDays: Set<string>, graceDays: Set<string> = new Set()): MonthCell[] {
  const now = new Date()
  now.setHours(0, 0, 0, 0)

  // #586: graceDays — пропуск без штрафа (вернулся раньше 58 ч): мостит, значка нет.
  const bridgeDays = new Set([...frozenDays, ...lifeLostDays, ...graceDays])

  const firstOfMonth = new Date(year, month, 1)
  const lastOfMonth = new Date(year, month + 1, 0)
  const daysInMonth = lastOfMonth.getDate()

  // dow: 0=Sun..6=Sat → convert to Mon-first 0..6
  const firstDow = (firstOfMonth.getDay() + 6) % 7

  const cells: MonthCell[] = []
  for (let i = 0; i < firstDow; i++) {
    cells.push({ date: null, dayNumber: 0, inStreak: false, frozen: false, lifeLost: false, daysIntoStreak: 0, isToday: false, isFuture: false })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d)
    date.setHours(0, 0, 0, 0)
    const diffDays = Math.round((now.getTime() - date.getTime()) / 86400000)
    const isFuture = diffDays < 0
    const isToday = diffDays === 0
    const inStreak = !isFuture && activeDays.has(fmtDay(date))
    const frozen = !isFuture && !inStreak && frozenDays.has(fmtDay(date))
    const lifeLost = !isFuture && !inStreak && !frozen && lifeLostDays.has(fmtDay(date))
    const daysIntoStreak = inStreak ? runLengthEndingAt(date, activeDays, bridgeDays) : 0
    cells.push({ date, dayNumber: d, inStreak, frozen, lifeLost, daysIntoStreak, isToday, isFuture })
  }
  return cells
}

export function StreakHistorySheet({ open, onClose, streakDays, streakRecord, lives }: Props) {
  const dragControls = useDragControls()

  // Start with the month containing today; allow stepping back through history.
  const now = useMemo(() => new Date(), [])
  const [offset, setOffset] = useState(0) // 0 = current month, -1 = previous month, etc.

  const viewYear = now.getFullYear()
  const viewMonth = now.getMonth() + offset

  // Normalize when month < 0 or > 11
  const realDate = new Date(viewYear, viewMonth, 1)
  const year = realDate.getFullYear()
  const month = realDate.getMonth()

  // Реальная история активных дней (для прошлых серий). Грузим при открытии.
  const { data: activeData } = useQuery({
    queryKey: ['streak', 'active-days'],
    queryFn: () => api.getActiveDays(),
    enabled: open,
    // staleTime 0: после сдачи отчёта окно должно сразу показывать огонёк на
    // сегодняшнем дне (кэш с прошлого открытия — без сегодняшней даты).
    staleTime: 0,
  })
  const activeDays = useMemo(() => new Set(activeData?.days ?? []), [activeData])
  // #552: рекорд приходит вместе с историей — у «старых» клиентов сервер
  // восстанавливает его по прошлым сериям при первом открытии. Пропса хватает
  // только до ответа, поэтому берём наибольшее из известного.
  const recordToShow = Math.max(streakRecord ?? 0, (activeData as { streakRecord?: number } | undefined)?.streakRecord ?? 0, streakDays)
  // #532: дни паузы программы попадают в ту же корзину, что личная заморозка —
  // для клиента это одна и та же «заморозка», и мостят серию они одинаково.
  const frozenDays = useMemo(
    () => new Set([...(activeData?.frozenDays ?? []), ...(activeData?.pausedDays ?? [])]),
    [activeData],
  )
  const lifeLostDays = useMemo(() => new Set(activeData?.lifeLostDays ?? []), [activeData])
  const graceDays = useMemo(() => new Set(activeData?.graceDays ?? []), [activeData])
  // #339: вехи, уже полученные по ledger (бонус выдаётся один раз за жизнь).
  const claimedMilestones = useMemo(() => activeData?.claimedMilestones ?? [], [activeData])

  const cells = useMemo(() => buildMonth(year, month, activeDays, frozenDays, lifeLostDays, graceDays), [year, month, activeDays, frozenDays, lifeLostDays, graceDays])

  // Ближайший milestone и календарная дата, когда он будет достигнут, если
  // продолжать серию (для отметки «здесь будет монетка»). #339: пропускаем
  // уже полученные вехи — после обрыва серии цель не «7д снова», а первая
  // невыданная (иначе обещали монету, которую grantTuCoin не выдаст).
  const nextMilestoneDay = nextStreakMilestone(streakDays, claimedMilestones)?.day ?? null
  const upcomingCoinDay = useMemo(() => {
    if (nextMilestoneDay == null) return null
    const d = new Date(); d.setHours(0, 0, 0, 0)
    d.setDate(d.getDate() + (nextMilestoneDay - streakDays))
    return fmtDay(d)
  }, [nextMilestoneDay, streakDays])

  // Aggregate stats for this month
  const monthStats = useMemo(() => {
    let active = 0
    for (const c of cells) if (c.inStreak) active++
    return { active }
  }, [cells])

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  // Don't allow stepping forward past today's month
  const canGoForward = offset < 0

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="streak-history-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragListener={false} dragControls={dragControls}
            dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            <div style={{ position: 'relative', padding: '8px 20px 4px' }}>
              <button
                data-testid="streak-history-close"
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 4, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>

              {/* Hero */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingTop: 4, paddingRight: 40 }}>
                <div style={{
                  width: 62, height: 62, borderRadius: 18,
                  background: streakDays >= 21
                    ? 'linear-gradient(135deg,#FF3B30,#C70021)'
                    : streakDays >= 7
                      ? 'linear-gradient(135deg,#FF9500,#FF6B00)'
                      : 'linear-gradient(135deg,#FFD60A,#FF9500)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 8px 22px rgba(255,107,0,0.35), 0 0 0 1.5px rgba(255,255,255,0.6) inset',
                }}>
                  <Flame size={32} color="white" fill="rgba(255,255,255,0.85)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA5B4', letterSpacing: 1, textTransform: 'uppercase' }}>
                    В режиме
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
                    <span style={{ fontSize: 32, fontWeight: 900, color: '#1C2D41', lineHeight: 1 }}>{streakDays}</span>
                    <span style={{ fontSize: 14, color: '#6B7B8D', fontWeight: 600 }}>
                      {streakDays === 1 ? 'день' : streakDays >= 2 && streakDays <= 4 ? 'дня' : 'дней'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: '#6B7B8D' }}>
                    <span>🏆 Рекорд: <b style={{ color: '#1C2D41' }}>{recordToShow}</b></span>
                    <span>❤️ Жизни: <b style={{ color: '#1C2D41' }}>{lives}/3</b></span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '6px 16px 0', minHeight: 0 }}>
              {/* Month switcher */}
              <div style={{
                background: 'white', borderRadius: 16,
                border: '1px solid rgba(26,58,92,0.06)',
                marginBottom: 12,
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderBottom: '1px solid rgba(26,58,92,0.06)',
                }}>
                  <button
                    data-testid="streak-prev-month"
                    onClick={() => setOffset((o) => o - 1)}
                    aria-label="Предыдущий месяц"
                    style={{
                      width: 32, height: 32, borderRadius: 10,
                      border: 'none', background: 'rgba(26,58,92,0.05)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <ChevronLeft size={18} color="#1C2D41" />
                  </button>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#1C2D41' }}>
                      {MONTH_NAMES[month]} {year}
                    </div>
                    <div style={{ fontSize: 10, color: '#9AA5B4', marginTop: 1 }}>
                      🔥 {monthStats.active} {monthStats.active === 1 ? 'день' : monthStats.active >= 2 && monthStats.active <= 4 ? 'дня' : 'дней'} в этом месяце
                    </div>
                  </div>
                  <button
                    data-testid="streak-next-month"
                    onClick={() => canGoForward && setOffset((o) => o + 1)}
                    disabled={!canGoForward}
                    aria-label="Следующий месяц"
                    style={{
                      width: 32, height: 32, borderRadius: 10,
                      border: 'none', background: canGoForward ? 'rgba(26,58,92,0.05)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: canGoForward ? 'pointer' : 'default',
                      opacity: canGoForward ? 1 : 0.25,
                    }}
                  >
                    <ChevronRight size={18} color="#1C2D41" />
                  </button>
                </div>

                {/* Weekday header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                  padding: '8px 10px 4px',
                  gap: 4,
                }}>
                  {WEEKDAYS.map((w, i) => (
                    <div
                      key={w}
                      style={{
                        textAlign: 'center',
                        fontSize: 10, fontWeight: 700,
                        color: i >= 5 ? '#FF6B00' : '#9AA5B4',
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                      }}
                    >
                      {w}
                    </div>
                  ))}
                </div>

                {/* Days grid */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 4, padding: '4px 10px 12px',
                }}>
                  {cells.map((c, i) => {
                    if (!c.date) {
                      return <div key={i} style={{ aspectRatio: '1 / 1' }} />
                    }
                    const b = fireBucket(c.daysIntoStreak)
                    const s = BUCKET_STYLE[b]
                    const iso = fmtDay(c.date)
                    // Монетка: получена на milestone-дне серии; будет получена — на
                    // расчётной дате следующего milestone (если серию продолжать).
                    const coinEarned = c.inStreak && MILESTONE_DAYS.has(c.daysIntoStreak)
                    const coinUpcoming = !coinEarned && !c.inStreak && upcomingCoinDay === iso
                    return (
                      <div
                        key={i}
                        title={`${c.date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}${c.inStreak ? ` · день ${c.daysIntoStreak}` : c.frozen ? ' · заморозка (день спасён)' : c.lifeLost ? ' · −1 жизнь (день оплачен жизнью)' : ''}${coinEarned ? ' · монета получена' : coinUpcoming ? ' · здесь будет монета' : ''}`}
                        style={{
                          aspectRatio: '1 / 1',
                          borderRadius: 10,
                          background: c.inStreak ? s.bg : c.frozen ? 'rgba(74,144,226,0.14)' : c.lifeLost ? 'rgba(255,59,48,0.10)' : 'rgba(26,58,92,0.04)',
                          boxShadow: c.inStreak ? s.glow : c.frozen ? '0 0 0 1px rgba(74,144,226,0.30)' : c.lifeLost ? '0 0 0 1px rgba(255,59,48,0.28)' : 'none',
                          border: c.isToday ? '2px solid #1C2D41' : coinUpcoming ? '1.5px dashed rgba(74,108,247,0.5)' : 'none',
                          opacity: c.isFuture && !coinUpcoming ? 0.35 : 1,
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        <span style={{
                          position: 'absolute', top: 3, left: 4,
                          fontSize: 9, fontWeight: 700,
                          color: c.inStreak && b >= 3 ? 'rgba(255,255,255,0.85)' : c.inStreak ? '#6B5C40' : c.frozen ? '#3B7CC4' : c.lifeLost ? '#D64541' : '#9AA5B4',
                          lineHeight: 1,
                        }}>
                          {c.dayNumber}
                        </span>
                        {/* Монетка в углу */}
                        {(coinEarned || coinUpcoming) && (
                          <span style={{ position: 'absolute', top: 2, right: 2, lineHeight: 0 }}>
                            <TuCoinIcon size={13} glow={false} style={{ opacity: coinEarned ? 1 : 0.45 }} />
                          </span>
                        )}
                        {c.inStreak && (
                          <Flame
                            size={b >= 3 ? 18 : 14}
                            color={s.color}
                            fill={s.fill}
                          />
                        )}
                        {c.frozen && (
                          <Snowflake size={14} color="#4A90E2" />
                        )}
                        {/* #290: пропуск, «оплаченный» жизнью — серия не прервалась */}
                        {c.lifeLost && (
                          <HeartCrack data-testid="streak-cal-life-lost" size={14} color="#E0564B" />
                        )}
                        {/* Сегодня — какой день режима идёт */}
                        {c.isToday && streakDays > 0 && (
                          <span style={{
                            position: 'absolute', bottom: 1, left: 0, right: 0,
                            textAlign: 'center', fontSize: 7.5, fontWeight: 800,
                            color: c.inStreak && b >= 3 ? 'white' : '#1C2D41', lineHeight: 1,
                          }}>
                            день {streakDays}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Milestone-бонусы TuCoin — интерактивный ряд иконок (над «Шкалой огня») */}
              <MilestoneTrack streakDays={streakDays} claimed={claimedMilestones} />

              {/* Legend — «Шкала огня» под milestone-бонусами */}
              <div style={{
                background: 'white', borderRadius: 16, padding: 12,
                border: '1px solid rgba(26,58,92,0.06)',
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#1C2D41', marginBottom: 8, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                  Шкала огня
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                  {[
                    { b: 0 as const, label: 'пропуск' },
                    { b: 1 as const, label: '1–2 дн' },
                    { b: 2 as const, label: '3–6 дн' },
                    { b: 3 as const, label: '1–3 нед' },
                    { b: 4 as const, label: '21+ дн' },
                  ].map((it) => {
                    const s = BUCKET_STYLE[it.b]
                    return (
                      <div key={it.b} style={{ textAlign: 'center' }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: it.b === 0 ? 'rgba(26,58,92,0.04)' : s.bg,
                          boxShadow: it.b === 0 ? 'none' : s.glow,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          margin: '0 auto 4px',
                        }}>
                          {it.b > 0 && (
                            <Flame size={it.b >= 3 ? 16 : 12} color={s.color} fill={s.fill} />
                          )}
                        </div>
                        <span style={{ fontSize: 10, color: '#6B7B8D' }}>{it.label}</span>
                      </div>
                    )
                  })}
                </div>
                {/* Заморозка: спасённый пропуск — серия не рвётся, но день не в счёт */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(26,58,92,0.06)' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    background: 'rgba(74,144,226,0.14)', boxShadow: '0 0 0 1px rgba(74,144,226,0.30)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Snowflake size={14} color="#4A90E2" />
                  </div>
                  <span style={{ fontSize: 10, color: '#6B7B8D', lineHeight: 1.4 }}>
                    Заморозка спасла пропуск — серия не прервалась (этот день не идёт в счёт)
                  </span>
                </div>
                {/* #290: пропуск, оплаченный жизнью — серия рвётся только без жизней */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    background: 'rgba(255,59,48,0.10)', boxShadow: '0 0 0 1px rgba(255,59,48,0.28)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <HeartCrack size={14} color="#E0564B" />
                  </div>
                  <span style={{ fontSize: 10, color: '#6B7B8D', lineHeight: 1.4 }}>
                    Пропуск стоил жизни, но серия не прервалась — режим сбивается, только когда жизни кончились
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
